export type JsonPrimitive = string | number | boolean | null
export type JsonValue = JsonPrimitive | JsonObject | JsonValue[]
export type JsonObject = { [key: string]: JsonValue }
export type JsonStorageKind = 'object' | 'json-string'

export const isJsonObject = (value: unknown): value is JsonObject => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
)

export const isJsonValue = (value: unknown): value is JsonValue => {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') {
    return true
  }
  if (typeof value === 'number') {
    return Number.isFinite(value)
  }
  if (Array.isArray(value)) {
    return value.every(isJsonValue)
  }
  if (isJsonObject(value)) {
    return Object.values(value).every(isJsonValue)
  }
  return false
}

export const cloneJsonValue = (value: JsonValue): JsonValue => {
  if (Array.isArray(value)) {
    return value.map(cloneJsonValue)
  }
  if (isJsonObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => [key, cloneJsonValue(child)]),
    )
  }
  return value
}

export const parseJsonEditorValue = (value: unknown, storageKind: JsonStorageKind): JsonValue => {
  const parsed = storageKind === 'json-string'
    ? JSON.parse(String(value)) as unknown
    : value
  if (!isJsonValue(parsed)) {
    throw new Error('JSON value contains unsupported data')
  }
  return cloneJsonValue(parsed)
}

export const serializeJsonEditorValue = (
  value: JsonValue,
  storageKind: JsonStorageKind,
): JsonValue | string => {
  if (!isJsonValue(value)) {
    throw new Error('JSON value contains unsupported data')
  }
  if (storageKind === 'object') {
    return cloneJsonValue(value)
  }
  return JSON.stringify(value)
}

export const formatJsonEditorValue = (value: unknown, storageKind: JsonStorageKind): JsonValue => {
  return parseJsonEditorValue(value, storageKind)
}
